// src/pages/PropertyList/PropertyList.js
import React, { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  Pagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Paper,
  CircularProgress,
  Alert,
  Collapse,
  IconButton
} from '@mui/material'
import {
  Search,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material'
import { useSearchParams } from 'react-router-dom'
import { propertiesAPI } from '../../services/api'
import PropertyCard from '../../components/PropertyCard/PropertyCard'

// Estados brasileiros
const BRAZILIAN_STATES = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
]

const PropertyList = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [properties, setProperties] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [totalPages, setTotalPages] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [showFilters, setShowFilters] = useState(true)

  // Funções de formatação de número
  const formatNumber = (value) => {
    if (!value) return ''
    const num = value.toString().replace(/\D/g, '')
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }

  const parseFormattedNumber = (formatted) => {
    if (!formatted) return ''
    return formatted.replace(/\./g, '')
  }

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    property_type: searchParams.get('property_type') || '',
    transaction_type: searchParams.get('transaction_type') || '',
    city: searchParams.get('city') || '',
    state: searchParams.get('state') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    min_bedrooms: searchParams.get('min_bedrooms') || '',
    ordering: searchParams.get('ordering') || '-created_at'
  })

  // Estados para valores formatados de preço
  const [minPriceFormatted, setMinPriceFormatted] = useState(
    filters.min_price ? formatNumber(filters.min_price) : ''
  )
  const [maxPriceFormatted, setMaxPriceFormatted] = useState(
    filters.max_price ? formatNumber(filters.max_price) : ''
  )

  useEffect(() => {
    loadProperties()
  }, [currentPage, searchParams])

  const loadProperties = async () => {
    setLoading(true)
    setError('')

    try {
      const params = {
        page: currentPage,
        search: filters.search,
        property_type: filters.property_type,
        transaction_type: filters.transaction_type,
        city: filters.city,
        state: filters.state,
        ordering: filters.ordering
      }

      if (filters.min_price) params.price__gte = filters.min_price
      if (filters.max_price) params.price__lte = filters.max_price
      if (filters.min_bedrooms) params.bedrooms__gte = filters.min_bedrooms

      // Filtros vindos da Home (seções de destaque)
      const superFeatured = searchParams.get('is_super_featured')
      if (superFeatured) params.is_super_featured = superFeatured

      const boostType = searchParams.get('boost_type')
      if (boostType) params.boost_type = boostType

      const highlight = searchParams.get('highlight')
      if (highlight) params.highlight = highlight

      Object.keys(params).forEach((key) => {
        if (!params[key]) delete params[key]
      })

      const response = await propertiesAPI.list(params)
      setProperties(response.data.results || [])
      setTotalPages(Math.ceil((response.data.count || 0) / 20))
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error)
      setError('Erro ao carregar imóveis. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handleMinPriceChange = (e) => {
    const formatted = formatNumber(e.target.value)
    setMinPriceFormatted(formatted)
    const rawValue = parseFormattedNumber(formatted)
    handleFilterChange('min_price', rawValue)
  }

  const handleMaxPriceChange = (e) => {
    const formatted = formatNumber(e.target.value)
    setMaxPriceFormatted(formatted)
    const rawValue = parseFormattedNumber(formatted)
    handleFilterChange('max_price', rawValue)
  }

  const handleSearch = () => {
    const newParams = new URLSearchParams()

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value)
      }
    })

    setSearchParams(newParams)
    setCurrentPage(1)
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      property_type: '',
      transaction_type: '',
      city: '',
      state: '',
      min_price: '',
      max_price: '',
      min_bedrooms: '',
      ordering: '-created_at'
    })
    setMinPriceFormatted('')
    setMaxPriceFormatted('')
    setSearchParams({})
    setCurrentPage(1)
  }

  const handlePageChange = (event, value) => {
    setCurrentPage(value)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleFavoriteToggle = (propertyId) => {
    setProperties((prev) =>
      prev.map((property) =>
        property.id === propertyId
          ? { ...property, is_favorited: !property.is_favorited }
          : property
      )
    )
  }

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(
      (value) => value && value !== '-created_at'
    ).length
  }

  return (
    <Container maxWidth='xl'>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Typography variant='h4' component='h1' fontWeight='bold'>
          Buscar Imóveis
        </Typography>

        <Button
          startIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowFilters(!showFilters)}
          variant='outlined'
        >
          {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
          {getActiveFiltersCount() > 0 && (
            <Chip
              label={getActiveFiltersCount()}
              size='small'
              color='primary'
              sx={{ ml: 1 }}
            />
          )}
        </Button>
      </Box>

      {/* Filtros Colapsáveis */}
      <Collapse in={showFilters}>
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size='small'
                label='Buscar por título, descrição ou endereço'
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder='Ex: Apartamento no centro'
                sx={{ minWidth: 250 }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 'auto' }}>
              <FormControl size='small' sx={{ minWidth: 180 }}>
                <InputLabel>Tipo de Imóvel</InputLabel>
                <Select
                  value={filters.property_type}
                  label='Tipo de Imóvel'
                  onChange={(e) =>
                    handleFilterChange('property_type', e.target.value)
                  }
                >
                  <MenuItem value=''>Todos</MenuItem>
                  <MenuItem value='casa'>Casa</MenuItem>
                  <MenuItem value='apartamento'>Apartamento</MenuItem>
                  <MenuItem value='terreno'>Terreno</MenuItem>
                  <MenuItem value='comercial'>Comercial</MenuItem>
                  <MenuItem value='rural'>Rural</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 'auto' }}>
              <FormControl size='small' sx={{ minWidth: 150 }}>
                <InputLabel>Transação</InputLabel>
                <Select
                  value={filters.transaction_type}
                  label='Transação'
                  onChange={(e) =>
                    handleFilterChange('transaction_type', e.target.value)
                  }
                >
                  <MenuItem value=''>Todos</MenuItem>
                  <MenuItem value='venda'>Venda</MenuItem>
                  <MenuItem value='aluguel'>Aluguel</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 'auto' }}>
              <TextField
                size='small'
                label='Cidade'
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                placeholder='Ex: São Paulo'
                sx={{ minWidth: 180 }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 'auto' }}>
              <FormControl size='small' sx={{ minWidth: 180 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filters.state}
                  label='Estado'
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                >
                  <MenuItem value=''>Todos</MenuItem>
                  {BRAZILIAN_STATES.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 6, sm: 3, md: 'auto' }}>
              <TextField
                size='small'
                label='Preço Mín'
                value={minPriceFormatted}
                onChange={handleMinPriceChange}
                placeholder='Ex: 100.000'
                sx={{ minWidth: 140 }}
              />
            </Grid>

            <Grid size={{ xs: 6, sm: 3, md: 'auto' }}>
              <TextField
                size='small'
                label='Preço Máx'
                value={maxPriceFormatted}
                onChange={handleMaxPriceChange}
                placeholder='Ex: 500.000'
                sx={{ minWidth: 140 }}
              />
            </Grid>

            <Grid size={{ xs: 6, sm: 3, md: 'auto' }}>
              <TextField
                size='small'
                type='number'
                label='Min. Quartos'
                value={filters.min_bedrooms}
                onChange={(e) =>
                  handleFilterChange('min_bedrooms', e.target.value)
                }
                sx={{ minWidth: 140 }}
              />
            </Grid>

            <Grid size={{ xs: 6, sm: 3, md: 'auto' }}>
              <FormControl size='small' sx={{ minWidth: 160 }}>
                <InputLabel>Ordenar</InputLabel>
                <Select
                  value={filters.ordering}
                  label='Ordenar'
                  onChange={(e) => {
                    handleFilterChange('ordering', e.target.value)
                    handleSearch()
                  }}
                >
                  <MenuItem value='-created_at'>Mais Recentes</MenuItem>
                  <MenuItem value='created_at'>Mais Antigos</MenuItem>
                  <MenuItem value='price'>Menor Preço</MenuItem>
                  <MenuItem value='-price'>Maior Preço</MenuItem>
                  <MenuItem value='area'>Menor Área</MenuItem>
                  <MenuItem value='-area'>Maior Área</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant='outlined'
                  startIcon={<Clear />}
                  onClick={clearFilters}
                  disabled={getActiveFiltersCount() === 0}
                >
                  Limpar
                </Button>
                <Button
                  variant='contained'
                  startIcon={<Search />}
                  onClick={handleSearch}
                >
                  Buscar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Resultado */}
      <Box sx={{ mb: 2 }}>
        <Typography variant='body1' color='text.secondary'>
          {loading
            ? 'Carregando...'
            : `${properties.length} imóveis encontrados`}
        </Typography>
      </Box>

      {/* Lista de Imóveis - 5 por linha */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : error ? (
        <Alert severity='error' sx={{ mb: 4 }}>
          {error}
        </Alert>
      ) : properties.length === 0 ? (
        <Paper sx={{ p: 8, textAlign: 'center' }}>
          <Typography variant='h6' color='text.secondary' gutterBottom>
            Nenhum imóvel encontrado
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            Tente ajustar os filtros ou fazer uma nova busca
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {properties.map((property) => (
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 4,
                lg: 3,
                xl: 2
              }}
              key={property.id}
            >
              <PropertyCard
                property={property}
                onFavoriteToggle={handleFavoriteToggle}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color='primary'
            size='large'
          />
        </Box>
      )}
    </Container>
  )
}

export default PropertyList
